import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Database,
  Download,
  Trash2,
  RotateCcw,
  Plus,
  Settings,
  FolderOpen,
  Clock,
  HardDrive,
  AlertTriangle,
  RefreshCw,
  Calendar,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import api from '@/services/api';
import { PermissionGate } from '@/components/PermissionGate';
import { useToast } from '@/hooks/useToast';

interface Backup {
  id: string;
  filename: string;
  path: string;
  size: number;
  sizeFormatted: string;
  createdAt: string;
  type: 'auto' | 'manual';
}

interface BackupSettings {
  backupDirectory: string;
  networkPath: string;
  retentionDays: number;
  autoBackupEnabled: boolean;
  backupTime: string;
  lastBackup: string | null;
  copyToNetwork: boolean;
}

interface BackupData {
  backups: Backup[];
  settings: BackupSettings;
  totalCount: number;
  totalSize: number;
  totalSizeFormatted: string;
}

export default function BackupManagementPage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  
  const [showSettings, setShowSettings] = useState(false);
  const [settingsForm, setSettingsForm] = useState<Partial<BackupSettings>>({});
  const [confirmRestore, setConfirmRestore] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  // Fetch backups
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['backups'],
    queryFn: async () => {
      const response = await api.get('/system/backups');
      return response.data.data as BackupData;
    },
  });

  // Create backup mutation
  const createBackupMutation = useMutation({
    mutationFn: async () => {
      return api.post('/system/backups', { description: 'Manual backup from UI' });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['backups'] });
      toast({ title: 'Success', description: 'Backup created successfully' });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.response?.data?.error || 'Failed to create backup',
        variant: 'destructive',
      });
    },
  });

  // Delete backup mutation
  const deleteBackupMutation = useMutation({
    mutationFn: async (id: string) => {
      return api.delete(`/system/backups/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['backups'] });
      setConfirmDelete(null);
      toast({ title: 'Success', description: 'Backup deleted successfully' });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.response?.data?.error || 'Failed to delete backup',
        variant: 'destructive',
      });
    },
  });

  // Restore backup mutation
  const restoreBackupMutation = useMutation({
    mutationFn: async (id: string) => {
      return api.post(`/system/backups/${id}/restore`);
    },
    onSuccess: () => {
      setConfirmRestore(null);
      toast({
        title: 'Success',
        description: 'Database restored successfully. Please refresh the page.',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.response?.data?.error || 'Failed to restore backup',
        variant: 'destructive',
      });
    },
  });

  // Update settings mutation
  const updateSettingsMutation = useMutation({
    mutationFn: async (settings: Partial<BackupSettings>) => {
      return api.put('/system/backups/settings', settings);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['backups'] });
      setShowSettings(false);
      toast({ title: 'Success', description: 'Backup settings updated' });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.response?.data?.error || 'Failed to update settings',
        variant: 'destructive',
      });
    },
  });

  const handleDownload = (backup: Backup) => {
    window.open(`/api/system/backups/${backup.id}/download`, '_blank');
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString();
  };

  const backups = data?.backups || [];
  const settings = data?.settings;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Database className="w-8 h-8" />
            Backup Management
          </h1>
          <p className="text-muted-foreground mt-1">
            Create, manage, and restore database backups
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => refetch()}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button variant="outline" onClick={() => {
            setSettingsForm(settings || {});
            setShowSettings(true);
          }}>
            <Settings className="w-4 h-4 mr-2" />
            Settings
          </Button>
          <PermissionGate permission="backups.create">
            <Button onClick={() => createBackupMutation.mutate()} disabled={createBackupMutation.isPending}>
              {createBackupMutation.isPending ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Backup
                </>
              )}
            </Button>
          </PermissionGate>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100">
                <Database className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Backups</p>
                <p className="text-2xl font-bold">{data?.totalCount || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-100">
                <HardDrive className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Size</p>
                <p className="text-2xl font-bold">{data?.totalSizeFormatted || '0 MB'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-100">
                <Clock className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Last Backup</p>
                <p className="text-sm font-medium">
                  {settings?.lastBackup ? formatDate(settings.lastBackup) : 'Never'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={cn(
                "p-2 rounded-lg",
                settings?.autoBackupEnabled ? "bg-green-100" : "bg-yellow-100"
              )}>
                <Calendar className={cn(
                  "w-5 h-5",
                  settings?.autoBackupEnabled ? "text-green-600" : "text-yellow-600"
                )} />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Auto Backup</p>
                <p className="text-sm font-medium">
                  {settings?.autoBackupEnabled ? `Daily at ${settings.backupTime}` : 'Disabled'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Current Settings */}
      {settings && (
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4 text-sm">
              <div className="flex items-center gap-2">
                <FolderOpen className="w-4 h-4 text-muted-foreground" />
                <span className="text-muted-foreground">Local:</span>
                <code className="px-2 py-1 bg-muted rounded text-xs">{settings.backupDirectory}</code>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">Retention:</span>
                <span className="font-medium">{settings.retentionDays} days</span>
              </div>
              {settings.copyToNetwork && settings.networkPath && (
                <div className="flex items-center gap-2">
                  <HardDrive className="w-4 h-4 text-blue-500" />
                  <span className="text-muted-foreground">Network:</span>
                  <code className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs">{settings.networkPath}</code>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Backups Table */}
      <Card>
        <CardHeader>
          <CardTitle>Available Backups</CardTitle>
          <CardDescription>
            View and manage your database backups
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : backups.length === 0 ? (
            <div className="text-center py-8">
              <Database className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No backups found</p>
              <Button className="mt-4" onClick={() => createBackupMutation.mutate()}>
                Create Your First Backup
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-3 font-medium">Filename</th>
                    <th className="text-left p-3 font-medium">Type</th>
                    <th className="text-left p-3 font-medium">Size</th>
                    <th className="text-left p-3 font-medium">Created</th>
                    <th className="text-left p-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {backups.map((backup) => (
                    <tr key={backup.id} className="hover:bg-muted/50">
                      <td className="p-3">
                        <div>
                          <p className="font-medium font-mono text-sm">{backup.filename}</p>
                          <p className="text-xs text-muted-foreground truncate max-w-xs">{backup.path}</p>
                        </div>
                      </td>
                      <td className="p-3">
                        <span className={cn(
                          "px-2 py-1 text-xs font-medium rounded-full",
                          backup.type === 'auto' 
                            ? "bg-blue-100 text-blue-800" 
                            : "bg-green-100 text-green-800"
                        )}>
                          {backup.type === 'auto' ? 'Automatic' : 'Manual'}
                        </span>
                      </td>
                      <td className="p-3 font-medium">{backup.sizeFormatted}</td>
                      <td className="p-3 text-sm">{formatDate(backup.createdAt)}</td>
                      <td className="p-3">
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDownload(backup)}
                            title="Download"
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                          <PermissionGate permission="backups.restore">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setConfirmRestore(backup.id)}
                              title="Restore"
                              className="text-orange-600 hover:text-orange-700"
                            >
                              <RotateCcw className="w-4 h-4" />
                            </Button>
                          </PermissionGate>
                          <PermissionGate permission="backups.delete">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setConfirmDelete(backup.id)}
                              title="Delete"
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </PermissionGate>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5" />
                Backup Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="backupDirectory">Backup Directory</Label>
                <Input
                  id="backupDirectory"
                  value={settingsForm.backupDirectory || ''}
                  onChange={(e) => setSettingsForm({ ...settingsForm, backupDirectory: e.target.value })}
                  placeholder="/path/to/backups"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Full path where backups will be stored
                </p>
              </div>
              <div>
                <Label htmlFor="retentionDays">Retention Days</Label>
                <Input
                  id="retentionDays"
                  type="number"
                  min="1"
                  max="365"
                  value={settingsForm.retentionDays || 30}
                  onChange={(e) => setSettingsForm({ ...settingsForm, retentionDays: parseInt(e.target.value) })}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Backups older than this will be automatically deleted
                </p>
              </div>
              <div>
                <Label htmlFor="backupTime">Daily Backup Time</Label>
                <Input
                  id="backupTime"
                  type="time"
                  value={settingsForm.backupTime || '02:00'}
                  onChange={(e) => setSettingsForm({ ...settingsForm, backupTime: e.target.value })}
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="autoBackupEnabled"
                  checked={settingsForm.autoBackupEnabled !== false}
                  onChange={(e) => setSettingsForm({ ...settingsForm, autoBackupEnabled: e.target.checked })}
                  className="w-4 h-4"
                />
                <Label htmlFor="autoBackupEnabled">Enable automatic daily backups</Label>
              </div>

              {/* Network Storage Section */}
              <div className="border-t pt-4 mt-4">
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <FolderOpen className="w-4 h-4" />
                  Network/Remote Storage
                </h4>
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="copyToNetwork"
                      checked={settingsForm.copyToNetwork === true}
                      onChange={(e) => setSettingsForm({ ...settingsForm, copyToNetwork: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <Label htmlFor="copyToNetwork">Copy backups to network drive</Label>
                  </div>
                  <div>
                    <Label htmlFor="networkPath">Network Path / Mount Point</Label>
                    <Input
                      id="networkPath"
                      value={settingsForm.networkPath || ''}
                      onChange={(e) => setSettingsForm({ ...settingsForm, networkPath: e.target.value })}
                      placeholder="/mnt/network-share/backups or //server/share/backups"
                      disabled={!settingsForm.copyToNetwork}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Examples: /mnt/nas/backups, /media/usb/backups, //192.168.1.100/backups
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setShowSettings(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={() => updateSettingsMutation.mutate(settingsForm)}
                  disabled={updateSettingsMutation.isPending}
                >
                  {updateSettingsMutation.isPending ? 'Saving...' : 'Save Settings'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Restore Confirmation Modal */}
      {confirmRestore && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-md mx-4">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-orange-600" />
                </div>
                <h3 className="text-lg font-semibold">Restore Database</h3>
              </div>
              <p className="text-muted-foreground mb-4">
                <strong>Warning:</strong> This will replace the current database with the selected backup.
                A backup of the current database will be created before restoring.
              </p>
              <p className="text-sm text-muted-foreground mb-6">
                The application may need to be restarted after restore.
              </p>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setConfirmRestore(null)}>
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => restoreBackupMutation.mutate(confirmRestore)}
                  disabled={restoreBackupMutation.isPending}
                >
                  {restoreBackupMutation.isPending ? 'Restoring...' : 'Restore Database'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-md mx-4">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <Trash2 className="w-5 h-5 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold">Delete Backup</h3>
              </div>
              <p className="text-muted-foreground mb-6">
                Are you sure you want to delete this backup? This action cannot be undone.
              </p>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setConfirmDelete(null)}>
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => deleteBackupMutation.mutate(confirmDelete)}
                  disabled={deleteBackupMutation.isPending}
                >
                  {deleteBackupMutation.isPending ? 'Deleting...' : 'Delete Backup'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
